import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { authService } from "@/core/auth/services/authService";
import { WelcomeIntro } from "@/features/onboarding/components/WelcomeIntro";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";
import { EmailConfirmationIntro } from "../components/EmailConfirmationIntro";
import { userAction } from "@/core/telemetry/userAction";

const setupPath = "/?setup=start";

export function SignupPage() {
  const { session, loading: authLoading, signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const [notice, setNotice] = useState("");
  const submitting = useRef(false);

  useEffect(() => {
    if (!resendCooldown) return;
    const timeout = window.setTimeout(() => setResendCooldown(false), 60_000);
    return () => window.clearTimeout(timeout);
  }, [resendCooldown]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || authLoading) return;
    submitting.current = true;
    setError("");
    setLoading(true);
    try {
      const signupSession = await signUp(email.trim(), password);
      if (signupSession) {
        navigate(setupPath, { replace: true });
      } else {
        setEmail(email.trim());
        setPassword("");
        setAwaitingConfirmation(true);
        setResendCooldown(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't create your account. Please try again.");
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (submitting.current || resendCooldown || authLoading) return;
    submitting.current = true;
    setError("");
    setNotice("");
    setResending(true);
    setResendCooldown(true);
    try {
      await authService.resendSignupConfirmation(email);
      setNotice("Confirmation email requested. Check your inbox.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't resend your email. Please try again.");
    } finally {
      submitting.current = false;
      setResending(false);
    }
  };

  if (!authLoading && session) {
    return <Navigate to={setupPath} replace />;
  }

  if (awaitingConfirmation) {
    return (
      <Page>
        <SetupHeader title="Check your email" titleSize="welcome" centered />
        <PageContent className="flex flex-col [scrollbar-gutter:auto]">
          <EmailConfirmationIntro>
            <p>
              Open the confirmation link sent to
              <strong className="block break-all text-foreground">{email}</strong>
              then sign in to get started.
            </p>
            {error && <ErrorState message={error} />}
            {notice && <p role="status" className="text-primary">{notice}</p>}
            <SectionAction onClick={handleResend} disabled={resending || resendCooldown || authLoading}>
              {resending ? "Sending..." : resendCooldown ? "Resend available in a minute" : "Resend confirmation email"}
            </SectionAction>
          </EmailConfirmationIntro>
        </PageContent>
        <PageFooter narrow>
          <Button asChild size="touch">
            <Link to="/login" state={{ from: setupPath }}>Sign in</Link>
          </Button>
        </PageFooter>
      </Page>
    );
  }

  return (
    <FormPage
      header={<SetupHeader title="Welcome" titleSize="welcome" centered />}
      onSubmit={handleSubmit}
      narrow
      scrollable={false}
      contentClassName="flex h-full min-h-0 flex-col gap-4 space-y-0"
      actions={
        <Button type="submit" {...userAction("auth.signup_submit")} size="touch" disabled={loading || authLoading}>
          {loading ? "Creating account..." : "Let's get started"}
        </Button>
      }
    >
      <WelcomeIntro description="Let's get you started by creating your account." />
      <FieldGroup className="shrink-0" disabled={loading || authLoading}>
        <FormField id="signup-email" label="Email">
          <Input name="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
        </FormField>
        <FormField id="signup-password" label="Password">
          <Input name="password" type="password" autoComplete="new-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
        </FormField>
      </FieldGroup>
      {error && <div className="shrink-0"><ErrorState message={error} /></div>}
    </FormPage>
  );
}
